import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import UserAvatar from "app/components/UserAvatar";
import { useTranslation } from "react-i18next";
import ContactInformation from "./ContactInformation";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { cls } from "app/utils";
import {
  fetchCoachProfile,
  commonLocations,
} from "app/actions/coachNowCoachProfile";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useActions } from "app/utils/hooks";
import { Loading } from "app/components/Wrappers";
import { CoachProfileModel } from "app/models";
import SpaceCard from "app/components/SpaceCard";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import AthleteOffering, {
  PURCHASE_FAMILY_MEMBER_KEY,
} from "../Billing/AthleteOffering";
import PurchaseSuccessModal from "./PurchaseSuccessModal";
import useCoachBooking from "app/routes/Sessions/hooks/useCoachBooking";
import PendingPurchasesList from "app/components/Programs/PendingPurchasesList";
import ParticipantPickerModal from "app/routes/Sessions/ParticipantPickerModal";
import { useBookingParticipants } from "app/routes/Sessions/useBookingParticipants";
import { externalBookingUrl, safeExternalUrl } from "app/utils/externalUrl";

const CoachProfileInfo = () => {
  const { t } = useTranslation();
  const { coachId } = useParams<{ coachId: string }>();
  const userId = useSelector((state: RootState) => state.user.self.id);
  const history = useHistory();
  const coachActions = useActions({ fetchCoachProfile, commonLocations });

  const [coachData, setCoachData] = useState<CoachProfileModel>({});
  const [commonData, setCommonData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  const location = useLocation();
  const hasShownToast = useRef(false);
  const [purchaseTitle, setPurchaseTitle] = useState("");
  const [isSubscription, setIsSubscription] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  // CN-1323 (CFTB-3303): the family member the offering was purchased for,
  // stashed in sessionStorage before the Stripe redirect (the BE success URL
  // can't carry it). Empty for a self-purchase.
  const [purchaseFamilyMember, setPurchaseFamilyMember] = useState("");

  // CN-1323: "Who is this session for?" picker, gating the Book a session button.
  const { candidates, hasFamily, refetch } = useBookingParticipants();
  const [showParticipantPicker, setShowParticipantPicker] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const isSuccess = queryParams.get("payment");
    const purchasedTitle = queryParams.get("title");

    if (isSuccess === "success" && !hasShownToast.current) {
      hasShownToast.current = true;
      setPurchaseTitle(purchasedTitle || "");
      setIsSubscription(queryParams.get("offering_type") === "subscription");
      // CFTB-3303: read + clear the recipient stashed before the redirect.
      const familyMember = sessionStorage.getItem(PURCHASE_FAMILY_MEMBER_KEY);
      sessionStorage.removeItem(PURCHASE_FAMILY_MEMBER_KEY);
      setPurchaseFamilyMember(familyMember || "");
      setShowPurchaseModal(true);

      setTimeout(() => {
        queryParams.delete("payment");
        queryParams.delete("title");
        queryParams.delete("offering_type");
        history.replace({
          pathname: location.pathname,
          search: queryParams.toString(),
        });
      }, 1000);
    }

  }, [location.search, history]);

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        const resp = await coachActions.fetchCoachProfile(coachId);
        setCoachData(resp);
      } catch {
        history.push(`/404`);
      }
      setLoading(false);
    };

    const fetchConnection = async () => {
      const resp = await coachActions.commonLocations();
      setCommonData(resp);
    };

    Promise.all([fetchProfileData(), fetchConnection()]);
  }, [coachId, coachActions]);

  useEffect(() => {
    setProductsLoading(true);
    schedulingFetchGet(`/stripe_student_products?coach_id=${coachId}`)
      .then((data) => {
        if (!data) {
          throw new Error("Unexpected response format");
        }
        setProducts(
          data.filter(
            (product) =>
              product.enabled === true || product.subscribed === true,
          ),
        );
      })
      .catch((error) => {
        console.error("Failed to fetch products:", error);
      })
      .finally(() => setProductsLoading(false));
  }, [coachId, userId]);

  // Booking with this coach: the calendar opens on them, with their default
  // option already chosen.
  const { startBooking } = useCoachBooking();

  const proceedToBooking = (participantAthleteId: string | null) => {
    startBooking(coachId, participantAthleteId);
  };

  const onClickBookSession = () => {
    const link = externalBookingUrl(coachData?.bookingServiceLink);
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
      return;
    }
    // With managed family members, pick the participant first; otherwise book for self.
    if (hasFamily) {
      setShowParticipantPicker(true);
    } else {
      proceedToBooking(null);
    }
  };

  const handleConfirmParticipant = (participantAthleteId: string | null) => {
    setShowParticipantPicker(false);
    proceedToBooking(participantAthleteId);
  };

  const onClickPayment = () => {
    // The same field family as the booking link above, and the same treatment:
    // free text on the coach's profile, so parsed and allow-listed rather than
    // handed to `window.open` as it came. No CoachNow test here — there is no
    // in-app payment flow for it to fall back to.
    const link = safeExternalUrl(coachData?.paymentServiceLink);
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <Loading
        isLoading={loading}
        loadType="spinner"
        className={styles.loading_container}
      />
    );
  }

  return (
    <>
      <ParticipantPickerModal
        isOpen={showParticipantPicker}
        candidates={candidates}
        onClose={() => setShowParticipantPicker(false)}
        onConfirm={handleConfirmParticipant}
        onMemberAdded={refetch}
      />
      <section className={styles.profile_container}>
        {coachData?.coverUrl && (
          <div className={styles.cover_container}>
            <img
              src={coachData?.coverUrl}
              alt="cover"
              className={styles.cover_image}
            />
          </div>
        )}
        <div
          className={cls(styles.avatar_container, {
            [styles.avatar_without_cover]: !coachData?.coverUrl,
          })}
        >
          <UserAvatar
            userName={`${coachData?.firstName} ${coachData?.lastName}`}
            srcName={coachData?.avatarUrl as string}
            altName="cover"
            className={styles.avatar}
          />
          <span
            className={cls(styles.user_info_wrapper, {
              [styles.user_info_without_cover]: !coachData?.coverUrl,
            })}
          >
            {coachData?.firstName && (
              <p
                className={styles.user_name}
              >{`${coachData.firstName} ${coachData.lastName}`}</p>
            )}
            {coachData?.sport && (
              <p className={styles.sport}>{coachData.sport}</p>
            )}
          </span>
        </div>
      </section>
      <section
        className={cls(styles.about_container, {
          [styles.null_section]: !coachData?.about,
        })}
      >
        <div className={styles.about_container_content}>
          {coachData?.about && (
            <div
              className={cls(styles.about_wrapper, {
                [styles.about_container_without_cover]: !coachData?.coverUrl,
              })}
            >
              <label className={styles.about_title}>{t("About")}</label>
              <p className={styles.about_info}>{coachData?.about}</p>
            </div>
          )}
          {commonData.length > 0 && (
            <div className={styles.same_space_connections}>
              <p>{t("Spaces and Groups you’re both members of")}</p>
              <div className={styles.same_spaces_list}>
                {commonData.map((item) => {
                  return (
                    <SpaceCard
                      key={item.id}
                      className={styles.space_card}
                      spaceData={item}
                      imageWidth={80}
                      imageHeight={80}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div
          className={cls(styles.book_session_container, {
            [styles.book_sessions_without_cover]: !coachData?.coverUrl,
          })}
        >
          <span className={styles.buttons_container}>
            {/*
              The button offers the coach's own booking service, so it is shown when
              they have set one and not otherwise. It used to appear for every Academy
              coach as well, which put "Book a session" in front of athletes whose coach
              had nowhere for it to lead.
            */}
            {coachData?.bookingServiceLink && (
              <ButtonVariant
                title={t("Book a session")}
                buttonType="primary"
                buttonStyles={styles.book_content_btn}
                onClickFunc={onClickBookSession}
              />
            )}

            {coachData?.paymentServiceLink && (
              <ButtonVariant
                title={t("Make a payment")}
                buttonType="secondary-outline"
                buttonStyles={cls(
                  styles.book_content_btn,
                  styles.make_a_payment_btn,
                )}
                onClickFunc={onClickPayment}
              />
            )}
          </span>
          {(coachData?.bookingServiceLink || coachData?.paymentServiceLink) && (
            <hr className={styles.divider} />
          )}
          <div className={styles.contact_info_container}>
            <ContactInformation coachData={coachData} />
          </div>
        </div>
      </section>

      {productsLoading && (
        <Loading isLoading={true} loadType="spinner" className={styles.loading_container} />
      )}

      {!productsLoading && products.length > 0 && (
        <div>
          <h2 className={styles.subtitle}>{t("Offerings")}</h2>
          <PendingPurchasesList
            filterByPurchasableType="StripeProduct"
            filterByCoachId={coachId}
          />
          <div className={styles.offerings_container}>
            {products.map((product) => (
              <AthleteOffering
                key={product.id}
                product={product}
              />
            ))}
          </div>
        </div>
      )}

      <PurchaseSuccessModal
        isOpen={showPurchaseModal}
        subscriptionName={purchaseTitle}
        isSubscription={isSubscription}
        familyMemberName={purchaseFamilyMember}
        onClose={() => setShowPurchaseModal(false)}
        onViewPurchases={() => {
          setShowPurchaseModal(false);
          history.push("/account/athlete_subscriptions");
        }}
      />
    </>
  );
};

export default CoachProfileInfo;
